import {
	type LineObject,
	type SDK_Filter,
	type SDK_ViewStackQuery,
	cannotBeFirstElements,
	codeBlockControlChar,
	controlCharsObject,
	isControlChar,
	isKeyword,
	keywordsObject,
	notDisplayedKeywords,
} from "@quickpoint.me/shared";
import { type ReactElement, lazy } from "react";
// import "../lib/WobbleText.css";
import type {
	Elements,
	PageElementData,
	TapIdContainer,
} from "../@types/viewerati";
import ReactNDR from "../displayComponents/ReactNDR";
import AutoPlay from "../displayComponents/pageElements/AutoPlay";
import AutoSpace from "../displayComponents/pageElements/AutoSpace";
import Backdrop from "../displayComponents/pageElements/Background";
import Blank from "../displayComponents/pageElements/Blank";
import Box from "../displayComponents/pageElements/Box";
import Button from "../displayComponents/pageElements/Button";
import Cell from "../displayComponents/pageElements/Cell";
import ColumnTitle from "../displayComponents/pageElements/ColumnTitle";
import Figure from "../displayComponents/pageElements/Figure";
import Filter from "../displayComponents/pageElements/Filter";
import GridEnd from "../displayComponents/pageElements/GridEnd";
import Line from "../displayComponents/pageElements/Line";
import GoogleMap from "../displayComponents/pageElements/Map";
import MoveBy from "../displayComponents/pageElements/MoveBy";
import PageDivider from "../displayComponents/pageElements/PageDivider";
import Paragraph from "../displayComponents/pageElements/Paragraph";
import Pause from "../displayComponents/pageElements/Pause";
import Read from "../displayComponents/pageElements/Read";
import SplitPage from "../displayComponents/pageElements/SplitPage";
// import Shadertoy from "../displayComponents/pageElements/Shadertoy";
import StackDivider from "../displayComponents/pageElements/StackDivider";
import StickyHeader from "../displayComponents/pageElements/StickyHeader";
import StickyFooter from "../displayComponents/pageElements/StickyFooter";
import StoryName from "../displayComponents/pageElements/StoryName";
import SubTitle from "../displayComponents/pageElements/SubTitle";
import SuperTitle from "../displayComponents/pageElements/SuperTitle";
import Title from "../displayComponents/pageElements/Title";
import Video from "../displayComponents/pageElements/Video";
import AudioElement from "../displayComponents/pageElements/audio/Audio";
import Bullet from "../displayComponents/pageElements/bullets/Bullet";
import IndentedBullet from "../displayComponents/pageElements/bullets/IndentedBullet";
import Form from "../displayComponents/pageElements/form/Form";
import type { ElementProps } from "../functionalComponents/RenderPage";
import type { NDRCC } from "../state/accessibility";
import createImageUrl from "./createImageUrl";

const CodeBlock = lazy(
	() => import("../displayComponents/pageElements/CodeBlock"),
);
const Chart = lazy(() => import("../displayComponents/pageElements/Chart"));

/**
 * How to organize elements and tapping?
 * A pause: keyword hides every element that appears after it and reveals them in order as the user taps.
 * THe parser puts elements into "tap containers".
 * If we had code like:
 *    title: Lost
 *    image: emilioEstevez
 *    pause:
 *    image: keiferSutherland
 *    title: Boys
 *
 * the first title and image would go in the first tap container and the elements after
 * the pause: keyword would go in the next tap container. The app would show the first tap container when rendered
 * and the second tap container when the user taps.
 *
 * If the user is moving backwards all the tap containers are visible.
 */

export const elements: Elements = {
	"*:": Bullet,
	"**:": IndentedBullet,
	"#:": Bullet,
	"##:": IndentedBullet,
	"---": PageDivider,
	"===": StackDivider,
	"|": SplitPage,
	"-->": GridEnd,
	"[]": Cell,
	"^^^": StickyHeader,
	"vvv": StickyFooter,
	"```": CodeBlock,
	"audio:": AudioElement,
	"autoplay:": AutoPlay,
	"autospace:": AutoSpace,
	"background:": Backdrop,
	"blank:": Blank,
	"box:": Box,
	"button:": Button,
	"chart:": Chart,
	"columnTitle:": ColumnTitle,
	"filter:": Filter,
	"form:": Form,
	"image:": Figure, // watch me now..
	"line:": Line,
	"map:": GoogleMap,
	"moveBy:": MoveBy,
	"paragraph:": Paragraph,
	"read:": Read,
	"storyName:": StoryName,
	"subtitle:": SubTitle,
	"supertitle:": SuperTitle,
	"pause:": Pause,
	"title:": Title,
	"video:": Video,
};

export interface MapPageCodeArgs {
	currentFilter: SDK_Filter;
	editMode: boolean | null;
	audioFileMaps: SDK_ViewStackQuery["viewStack"]["audioFiles"];
	imageMaps: SDK_ViewStackQuery["viewStack"]["images"];
	dataFileMaps: SDK_ViewStackQuery["viewStack"]["dataFiles"];
	neueroDivergentReader: boolean | NDRCC;
	pageCode: LineObject[];
	pageIndex: number;
	showAllTapElements: boolean;
}

const mapPageCode = ({
	audioFileMaps,
	pageCode,
	currentFilter,
	dataFileMaps,
	editMode,
	pageIndex,
	imageMaps,
	neueroDivergentReader,
	showAllTapElements,
}: MapPageCodeArgs) => {
	let stickyHeaderReactElements: ReactElement<ElementProps>[] = [];
	let stickyFooterReactElements: ReactElement<ElementProps>[] = [];

	// by default all tap containers will be tapped/visible unless a pause: keyword is encountered
	let containerTapped = true;

	let tapIndex = 0;

	// init the first tapContainer
	// the ids used also match the states of each elements in the PageElementsFamily atom family.
	const tapIdContainer: TapIdContainer[] = [
		{ containerTapped: false, ids: [] },
	];

	// init the page elements holder
	const pageElements: PageElementData[] = [];

	const pageReactElements = pageCode.reduce(
		(
			filtered: ReactElement<ElementProps>[],
			el: LineObject,
			idx: number,
		) => {
			if (
				(el && isKeyword(el.type)) ||
				(el && isControlChar(el.type)) ||
				(el && el.type === codeBlockControlChar)
			) {
				const type = el.type;
				let content: ReactElement | string = el.content;
				if (
					neueroDivergentReader &&
					!isControlChar(el.type) &&
					el.type in keywordsObject &&
					(
						keywordsObject as Record<
							string,
							(typeof keywordsObject)[keyof typeof keywordsObject]
						>
					)[el.type].allowNdr &&
					typeof el.content === "string"
				) {
					content = <ReactNDR text={el.content} />;
				}

				const modifier = el.modifier;
				let background = "unset";
				let url = "";
				let altText = "";
				// if a tap element is present create a new container and hide everything.
				// unless it's in edit mode, then show everything
				if (!editMode && type === "pause:" && !showAllTapElements) {
					// in normal mode this is untapped but in edit mode and when going backwards it's tapped/visible.
					containerTapped = false;
					// if the first element is a tap element don't increment the tap index
					// and use the first tapContainer
					// or if it's an element that's not noramlly displayed dont' increment
					if (idx !== 0 || !notDisplayedKeywords.includes(type)) {
						tapIndex++;
						tapIdContainer[tapIndex] = {
							containerTapped,
							ids: [],
						};
					}
				}

				// if the user is moving backwards the containers will appear as tapped
				tapIdContainer[tapIndex].containerTapped = showAllTapElements
					? true
					: containerTapped;
				tapIdContainer[tapIndex].ids.push(idx);

				// images need extra info for their URL and alt Text
				if (type === "image:" && imageMaps) {
					const map = imageMaps.find((map) =>
						map ? map.userDefinedName === el.content : undefined,
					);
					if (map) {
						url = createImageUrl(map);
						if (map.altText !== null) altText = map.altText;
					}
				}

				// charts need extra info for their URL and alt Text
				if (type === "chart:" && dataFileMaps) {
					const map = dataFileMaps.find((map) =>
						map ? map.userDefinedName === el.content : undefined,
					);
					if (map) {
						url = map.url;
						if (map.altText !== null) altText = map.altText;
					}
				}

				// audio need extra info for their URL and alt Text
				if (type === "audio:" && audioFileMaps) {
					const map = audioFileMaps.find((map) =>
						map ? map.userDefinedName === el.content : undefined,
					);
					if (map) {
						url = map.url;
						if (map.altText !== null) altText = map.altText;
					}
				}

				// boxes need extra info for their URL and alt Text
				if (type === "box:" && modifier && imageMaps) {
					background = modifier["background:"]
						? modifier["background:"]
						: "unset";

					const map = imageMaps.find((map) =>
						map ? map.userDefinedName === background : undefined,
					);

					if (map) {
						background = `url(${createImageUrl(map)})`;
						if (map.altText !== null) altText = map.altText;
					}

					if (currentFilter && background in currentFilter.colors) {
						// @ts-expect-error: background isn't typed to index of the colors object
						background = currentFilter.colors[background];
					}
				}

				const elementData: PageElementData = {
					altText,
					background,
					content,
					firstElement: false,
					id: idx,
					lastElement: false,
					lineNumber: el.lineNumber,
					modifier,
					tapped: containerTapped,
					type: el.type,
					url,
				};

				pageElements[idx] = elementData;
				const Element = elements[type];

				filtered.push(
					<Blank
						key={`blank-autospace-element-${idx}-${pageIndex}`}
						isAutospaceElement={true}
						atomId={idx}
						type="blank:"
						pageIndex={pageIndex}
					/>,
				);
				filtered.push(
					<Element
						key={`element-${idx}-${pageIndex}`}
						atomId={idx}
						type={type}
						pageIndex={pageIndex}
					/>,
				);
			}
			return filtered;
		},
		[],
	);
	// //console.log(
	//   "pageElements:",
	//   pageElements,
	//   "pageReactElements:",
	//   pageReactElements,
	// );

	const stickyHeaderIndices: number[] = [];
	const stickyFooterIndices: number[] = [];
	pageReactElements.forEach((el, idx) => {
		if (el.props.type === controlCharsObject["^^^"].controlChar) {
			stickyHeaderIndices.push(idx);
		}
		if (el.props.type === controlCharsObject["vvv"].controlChar) {
			stickyFooterIndices.push(idx);
		}
	});

	// Splice from the end of the array to avoid index shifting issues
	if (stickyFooterIndices.length === 2) {
		stickyFooterReactElements = pageReactElements.splice(
			stickyFooterIndices[0],
			stickyFooterIndices[1] - stickyFooterIndices[0] + 1,
		);
		stickyFooterReactElements = stickyFooterReactElements.filter(
			(el) =>
				!el.props.isAutospaceElement &&
				el.props.type !== controlCharsObject["vvv"].controlChar,
		);
	}

	if (stickyHeaderIndices.length === 2) {
		stickyHeaderReactElements = pageReactElements.splice(
			stickyHeaderIndices[0],
			stickyHeaderIndices[1] - stickyHeaderIndices[0] + 1,
		);
		stickyHeaderReactElements = stickyHeaderReactElements.filter(
			(el) =>
				!el.props.isAutospaceElement &&
				el.props.type !== controlCharsObject["^^^"].controlChar,
		);
	}

	const first = pageElements.find(
		(el) => !cannotBeFirstElements.includes(el.type),
	);
	const last = pageElements.findLast(
		(el) => !cannotBeFirstElements.includes(el.type),
	);
	if (first) {
		first.firstElement = true;
	}

	if (last) {
		last.lastElement = true;
	}

	return {
		pageElements,
		pageReactElements,
		tapIdContainer,
		stickyHeaderReactElements,
		stickyFooterReactElements,
	};
};

export default mapPageCode;
